import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, Link, Divider, IconButton, Alert } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOffOutlined from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import AuthLayout, { authInputSx, authSubmitSx } from './AuthLayout';
import { auth } from '../../firebase';
import { GoogleAuthProvider, OAuthProvider, signInWithPopup } from 'firebase/auth';

const Login = () => {
  const { login, loading, isAuthenticated, setAuthFromServer } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(null); // 'google' | 'microsoft' | null

  // Land on the page the user originally asked for (ProtectedRoute passes it
  // via location state), falling back to the dashboard.
  const postLoginPath = location.state?.from?.pathname || '/dashboard';

  useEffect(() => {
    if (isAuthenticated && !loading) navigate(postLoginPath, { replace: true });
  }, [isAuthenticated, loading, navigate, postLoginPath]);

  // Only show full-screen spinner during initial auth bootstrap, not while submitting form
  if (loading && !authSubmitting && !oauthLoading) {
    return <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh' }}><CircularProgress/></Box>;
  }
  if (isAuthenticated) return null;

  const handleChange = (e) => {
    const { name, value } = e.target; setFormData(p => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setAuthSubmitting(true);
    try {
      const result = await login(formData.email, formData.password);
      if (!result.success) {
        setError(result.error || 'Invalid email or password');
      }
    } catch (err) {
      console.error('Login error', err);
      setError('Unexpected error');
    } finally { setAuthSubmitting(false); }
  };

  const exchange = async () => {
    if (!auth.currentUser) return;
    const idToken = await auth.currentUser.getIdToken();
    const API_BASE = `${process.env.REACT_APP_API_BASE || 'http://localhost:5000'}/api`;
    let data;
    try {
      const res = await fetch(`${API_BASE}/auth/firebase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ idToken })
      });
      const text = await res.text();
      try { data = JSON.parse(text); } catch { throw new Error('Non-JSON response from auth exchange'); }
    } catch (netErr) {
      console.error('Auth exchange error', netErr);
      setError('Network error reaching auth server');
      return;
    }
    if (data.token && data.user) {
      setAuthFromServer(data.token, data.user);
      navigate(postLoginPath, { replace: true });
    } else {
      setError(data.message || 'Third-party auth failed');
    }
  };

  const signInWithGoogle = async () => {
    try {
      setError('');
      setOauthLoading('google');
      const provider = new GoogleAuthProvider();
      // Force account chooser so user can pick a different Google account each time
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(auth, provider);
      await exchange();
    } catch (err) {
      console.error('Google sign-in error (raw):', err);
      let friendly = 'Google sign-in failed';
      const code = err?.code;
      switch(code){
        case 'auth/popup-blocked': friendly='Popup blocked. Allow popups and retry.'; break;
        case 'auth/popup-closed-by-user': friendly='Popup closed before completion.'; break;
        case 'auth/unauthorized-domain': friendly='Domain not authorized in Firebase console.'; break;
        case 'auth/invalid-api-key': friendly='Invalid Firebase API key env vars.'; break;
        case 'auth/network-request-failed': friendly='Network error during Google sign-in.'; break;
        case 'auth/account-exists-with-different-credential': friendly='This email already has an account with a different sign-in method. Sign in the way you originally signed up, then link Google from Settings.'; break;
        default: if(code && code.startsWith('auth/')) friendly += ` (${code})`;
      }
      setError(friendly);
    } finally { setOauthLoading(null); }
  };

  const signInWithMicrosoft = async () => {
    try {
      setError('');
      setOauthLoading('microsoft');
      const provider = new OAuthProvider('microsoft.com');
      provider.addScope('email'); provider.addScope('profile');
      await signInWithPopup(auth, provider);
      await exchange();
    } catch (err) {
      console.error(err); setError('Microsoft sign-in failed');
    } finally { setOauthLoading(null); }
  };

  return (
    <AuthLayout title="Welcome back" subtitle="Sign in to continue" busy={!!(authSubmitting || oauthLoading)}>
      <Box sx={{ display:'flex', gap:1.2, flexDirection:{ xs:'column', sm:'row' }, mt:0.5 }}>
        <Button onClick={signInWithGoogle} variant="outlined" startIcon={<GoogleIcon/>} disabled={!!oauthLoading || authSubmitting} sx={{ flex:1, textTransform:'none', fontWeight:600, py:1 }}>{oauthLoading==='google'?<CircularProgress size={20}/>: 'Google'}</Button>
        <Button onClick={signInWithMicrosoft} variant="outlined" startIcon={<MicrosoftIcon/>} disabled={!!oauthLoading || authSubmitting} sx={{ flex:1, textTransform:'none', fontWeight:600, py:1 }}>{oauthLoading==='microsoft'?<CircularProgress size={20}/>: 'Microsoft'}</Button>
      </Box>
      <Divider sx={{ my:1.5 }}><Typography variant="caption" sx={{ letterSpacing:1 }}>OR</Typography></Divider>
      {error && <Alert severity="error" role="alert" sx={{ borderRadius: 2 }}>{error}</Alert>}
      <form onSubmit={handleSubmit} noValidate style={{ display:'flex', flexDirection:'column', gap:16 }}>
        <Box sx={{ display:'flex', flexDirection:'column', gap:1.75 }}>
          <Box sx={{ display:'flex', flexDirection:'column', gap:0.75 }}>
            <Typography variant="subtitle2" sx={{ fontWeight:600 }} component="label" htmlFor="login-email">Email</Typography>
            <TextField id="login-email" fullWidth name="email" type="email" placeholder="you@example.com" value={formData.email} onChange={handleChange} required size="small" autoComplete="email" autoFocus disabled={loading || authSubmitting || !!oauthLoading} InputProps={{ sx: authInputSx }} />
          </Box>
          <Box sx={{ display:'flex', flexDirection:'column', gap:0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight:600 }} component="label" htmlFor="login-password">Password</Typography>
            <TextField id="login-password" fullWidth name="password" type={showPassword?'text':'password'} placeholder="••••••••" value={formData.password} onChange={handleChange} required size="small" autoComplete="current-password" disabled={loading || authSubmitting || !!oauthLoading} InputProps={{ endAdornment:(<IconButton size="small" onClick={()=>setShowPassword(p=>!p)} aria-label={showPassword?'Hide password':'Show password'} edge="end" sx={{ p:0.5 }}>{showPassword? <VisibilityOffOutlined sx={{ fontSize:18 }}/> : <VisibilityOutlined sx={{ fontSize:18 }}/>}</IconButton>), sx: authInputSx }} />
            <Link component={RouterLink} to="/forgot-password" underline="hover" sx={{ fontSize:'0.8125rem', lineHeight:1.2, mt:0.5, alignSelf:'flex-start' }}>Forgot password?</Link>
          </Box>
        </Box>
        <Button type="submit" fullWidth variant="contained" size="large" startIcon={<LockOutlinedIcon/>} disabled={loading || authSubmitting || !!oauthLoading} sx={authSubmitSx}>{(loading || authSubmitting) ? <CircularProgress size={22} color="inherit" /> : 'Sign in'}</Button>
      </form>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign:'center', lineHeight:1.4, fontSize:'0.8125rem', mt:2 }}>
        By continuing you agree to our <Link component={RouterLink} to="/terms">Terms</Link> and <Link component={RouterLink} to="/privacy">Privacy Policy</Link>.
      </Typography>
      <Typography variant="body2" sx={{ textAlign:'center', fontSize:'0.8125rem', mt:1 }}>Don't have an account? <Link component={RouterLink} to="/register" underline="hover" sx={{ fontWeight:600, fontSize:'0.8125rem' }}>Sign up</Link></Typography>
    </AuthLayout>
  );
};

export default Login;
